import React from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'
import { ErrorState, type ErrorStateSize } from '@/components/common/ErrorState'

export interface ErrorBoundaryProps {
  children: React.ReactNode
  /** Replaces the default ErrorState entirely. Receives the error and a reset callback. */
  fallback?: React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode)
  /** Visual weight of the default fallback. */
  size?: ErrorStateSize
  /** Names the failed region so the headline reads "Shift schedule failed to load". */
  label?: string
  /** Reported alongside the error — useful once a logging service is wired in. */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  /** Any change to a value here clears the error. The router key uses this. */
  resetKey?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class ErrorBoundaryInner extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Without this a boundary that caught an error stays broken forever: React
    // never re-renders the children, so navigating away leaves the fallback up.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  reset = (): void => {
    this.setState({ hasError: false, error: undefined })
  }

  render(): React.ReactNode {
    const { hasError, error } = this.state
    if (!hasError) return this.props.children

    const { fallback, size = 'section', label } = this.props
    if (typeof fallback === 'function') {
      return fallback(error ?? new Error('Unknown error'), this.reset)
    }
    if (fallback) return fallback

    return (
      <ErrorState
        error={error}
        fallbackKind="crash"
        size={size}
        title={label ? `${label} failed to load` : undefined}
        onRetry={this.reset}
      />
    )
  }
}

/**
 * Catches render errors in the subtree and shows a recoverable fallback instead
 * of a blank screen. Resets automatically on navigation, so a crash on one route
 * never strands the user on the next.
 *
 * Router presence is checked before reading the location: `useLocation()` throws
 * when no Router is mounted, which would make this component fail in exactly the
 * situation it exists to handle. Outside a Router it degrades to a boundary with
 * no automatic reset rather than crashing.
 */
export function ErrorBoundary(props: ErrorBoundaryProps): React.ReactElement {
  const inRouter = useInRouterContext()
  return inRouter ? (
    <RouteAwareErrorBoundary {...props} />
  ) : (
    <ErrorBoundaryInner {...props} />
  )
}

function RouteAwareErrorBoundary(props: ErrorBoundaryProps): React.ReactElement {
  const { pathname } = useLocation()
  return <ErrorBoundaryInner {...props} resetKey={props.resetKey ?? pathname} />
}

ErrorBoundary.displayName = 'ErrorBoundary'

/**
 * Boundary for trees rendered outside the router (or before it mounts), where
 * `useLocation` would throw. Reset is manual.
 */
export const StandaloneErrorBoundary = ErrorBoundaryInner
